/**
 * Central API configuration for the Avator frontend.
 * Base URL can be overridden via REACT_APP_API_BASE_URL in .env
 */

const DEFAULT_BASE_URL =
  "https://us-central1-retail-jkommerce-poc.cloudfunctions.net";

/** Backend base URL (Cloud Functions) */
export const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || DEFAULT_BASE_URL;

/**
 * Orbiee agent HTTP API + WebSocket host (e.g. http://127.0.0.1:8000).
 * In development, default to local FastAPI agent so WS does not mistakenly
 * target the CRA dev server (port 3000). Production builds must set env or
 * rely on same-origin reverse proxy via window.location when appropriate.
 */
function resolveOrbieeAgentBaseUrl() {
 return process.env.REACT_APP_ORBIEE_AGENT_BASE_URL || "";
}

export const ORBIEE_AGENT_BASE_URL = resolveOrbieeAgentBaseUrl();

const ORBIEE_WS_ENV = (
  process.env.REACT_APP_ORBIEE_WS_BASE_URL || ""
).replace(/\/$/, "");

function wsBaseFromHttp(httpBase) {
  if (!httpBase) return "";
  if (httpBase.startsWith("https://")) return `wss://${httpBase.slice(8)}`;
  if (httpBase.startsWith("http://")) return `ws://${httpBase.slice(7)}`;
  return httpBase;
}

/** WebSocket origin for agent chat (defaults to ws(s) from `ORBIEE_AGENT_BASE_URL`). */
export const ORBIEE_WS_BASE_URL =
  ORBIEE_WS_ENV || wsBaseFromHttp(ORBIEE_AGENT_BASE_URL);

/** Full WS URL for `/ws/chat/{userId}?device_id={deviceId}` (optional `&` query from caller). */
export function orbieeChatWsUrl(userId, deviceId) {
  const base = ORBIEE_WS_BASE_URL.replace(/\/$/, "");
  const path = `/ws/chat/${encodeURIComponent(String(userId))}?device_id=${encodeURIComponent(String(deviceId))}`;
  if (!base) return path;
  return `${base}${path}`;
}

/** Always use WebSocket chat — Cloud Function fallback removed. */
export const USE_ORBIEE_WEBSOCKET_CHAT = true;

/**
 * Frontend ↔ Agent UI sync (`ui_event` frames + `screen_context` on
 * start/voice). Set REACT_APP_UI_AGENT_SYNC=false to disable without
 * touching code (kill switch if the backend contract is not deployed).
 */
export const UI_AGENT_SYNC_ENABLED =
  process.env.REACT_APP_UI_AGENT_SYNC !== "false";

/** Helper to build full URL from path (e.g. "/login_v2" or "login_v2") */
export const apiUrl = (path) => {
  const p = path.startsWith("/") ? path : `/${path}`;
  return `${API_BASE_URL}${p}`;
};

/** Orbiee FastAPI backend (cart, avatar, WS host, etc.) */
export const ORBIEE_BACKEND_BASE_URL =
  process.env.REACT_APP_ORBIEE_BACKEND_BASE_URL ||
  "https://orbiee-backend-dev-218142584790.us-central1.run.app";

export const apiUrl_backend = (path) => {
  const base = ORBIEE_BACKEND_BASE_URL.replace(/\/$/, "");
  const p = path.startsWith("/") ? path : `/${path}`;
  return `${base}${p}`;
};
// ======================
// Backend API paths (full URLs via apiUrl())
// ======================

/** Auth */
export const LOGIN_URL = apiUrl("login_v2");
export const LOGOUT_URL = apiUrl("logout");

/** Avatar & realtime */
export const AVATAR_REALTIME_URL = "https://orbiee-backend-dev-218142584790.us-central1.run.app/avatar_realtime";

export const CHAT_AVATAR_URL = apiUrl("chat_avatar");

/** Chat / AI */
export const TTS_URL = apiUrl("tts_function");
export const STT_URL = apiUrl("stt_function");

/** Products & catalog */
export const PRODUCTS_V1_URL = apiUrl("products_v1");
export const PRODUCTS_V1_DEV_URL = apiUrl("products_v1_dev");
export const PRODUCT_ACCESSORIES_URL = apiUrl("product_accessories");
export const PRODUCT_ACCESSORIES_DEV_URL = apiUrl("product_accessories_dev");
export const PRODUCT_ONSALE_URL = apiUrl("product_onsale");
export const PRODUCT_ONSALE_DEV_URL = apiUrl("product_onsale_dev");
export const NEW_ARRIVALS_URL = apiUrl("new_arrivals_dev");
export const PRODUCT_CATEGORY_URL = apiUrl("product_category");
export const PRODUCT_CATEGORY_DEV_URL = apiUrl("product_category_dev");
export const GENERAL_DETAIL_PAGE_URL = apiUrl("general_detail_page");
export const COMPLETE_THE_LOOK_URL = apiUrl("complete_the_look");
export const DETAIL_PAGE_VIEW_URL = apiUrl("detail_page_view");
export const VIRTUAL_TRY_ON_URL = "https://orbiee-backend-dev-218142584790.us-central1.run.app/virtual_try_on";

/** Home & recommendations */
export const HOME_PAGE_URL = apiUrl("home_page");
export const FEATURED_PRODUCTS_URL = apiUrl("featured_products");
export const PROMOTIONS_URL = apiUrl("promotions");
export const RECOMMENDED_FOR_YOU_URL = apiUrl("recommended_for_you");

/** Search & filters */
export const FILTER_OPTIONS_URL = apiUrl("filter_options");
export const PRODUCT_SEARCH_URL = apiUrl("product_search");
export const SEARCH_PRODUCTS_URL = apiUrl("search_products");

/** User & profile */
export const USERS_URL = apiUrl("users");
export const CUSTOMER_PROFILE_URL = apiUrl("customer_profile");
export const CUSTOMER_PROFILE_UPDATE_URL = apiUrl("customer_profile_update");
export const CUSTOMER_ORDERS_V1_URL = apiUrl("customer_orders_v1");

/** Cart — legacy Cloud Function (deprecated; kept for reference) */
export const CART_OPERATIONS_URL = apiUrl("cart_operations");

/** Cart — REST API (GET/POST/PUT/DELETE /api/cart) */
export const CART_API_URL = apiUrl_backend("/api/cart");

/** Wishlist — legacy Cloud Function (deprecated; kept for reference) */
// export const WISHLIST_OPERATIONS_URL = apiUrl("wishlist_operations_dev");
export const WISHLIST_OPERATIONS_URL = "";

/** Wishlist — REST API (GET/POST/DELETE /api/wishlist) */
export const WISHLIST_API_URL = apiUrl_backend("/api/wishlist");

/** Admin */
export const UPDATE_USER_PREFERENCES_V1_URL = apiUrl(
  "update_user_preferences_v1"
);
export const DELETE_USER_URL = apiUrl("delete_user");
export const GET_ALL_PRODUCTS_URL = apiUrl("get_all_products");

/** Create user (auth flow) */
export const CREATE_USER_V1_URL = apiUrl("create_user_v1");

// ======================
// External APIs
// ======================

export const ELEVENLABS_SPEECH_TO_TEXT_URL =
  "https://api.elevenlabs.io/v1/speech-to-text";

export const D_ID_API_BASE = "https://api.d-id.com";

// ======================
// Default export (all config)
// ======================

const apiConfig = {
  API_BASE_URL,
  ORBIEE_AGENT_BASE_URL,
  ORBIEE_WS_BASE_URL,
  orbieeChatWsUrl,
  USE_ORBIEE_WEBSOCKET_CHAT,
  UI_AGENT_SYNC_ENABLED,
  apiUrl,
  LOGIN_URL,
  LOGOUT_URL,
  AVATAR_REALTIME_URL,
  CHAT_AVATAR_URL,
  TTS_URL,
  STT_URL,
  PRODUCTS_V1_URL,
  PRODUCTS_V1_DEV_URL,
  PRODUCT_ACCESSORIES_URL,
  PRODUCT_ACCESSORIES_DEV_URL,
  PRODUCT_ONSALE_URL,
  PRODUCT_ONSALE_DEV_URL,
  NEW_ARRIVALS_URL,
  PRODUCT_CATEGORY_URL,
  PRODUCT_CATEGORY_DEV_URL,
  GENERAL_DETAIL_PAGE_URL,
  COMPLETE_THE_LOOK_URL,
  DETAIL_PAGE_VIEW_URL,
  VIRTUAL_TRY_ON_URL,
  HOME_PAGE_URL,
  FEATURED_PRODUCTS_URL,
  PROMOTIONS_URL,
  RECOMMENDED_FOR_YOU_URL,
  FILTER_OPTIONS_URL,
  PRODUCT_SEARCH_URL,
  SEARCH_PRODUCTS_URL,
  USERS_URL,
  CUSTOMER_PROFILE_URL,
  CUSTOMER_PROFILE_UPDATE_URL,
  CUSTOMER_ORDERS_V1_URL,
  CART_OPERATIONS_URL,
  CART_API_URL,
  ORBIEE_BACKEND_BASE_URL,
  WISHLIST_OPERATIONS_URL,
  WISHLIST_API_URL,
  UPDATE_USER_PREFERENCES_V1_URL,
  DELETE_USER_URL,
  GET_ALL_PRODUCTS_URL,
  CREATE_USER_V1_URL,
  ELEVENLABS_SPEECH_TO_TEXT_URL,
  D_ID_API_BASE,
};

export default apiConfig;
